import { type UploadStorageProviderType } from '@/features/spark-storage/types';
import { sparkApi } from '@api/spark-client';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const s3Api = axios.create({});
const sharepointApi = axios.create({});

export interface Document {
  id: string;
  content_version_id: string;
  name: string;
  site_id: string;
  created_date: string;
  last_modified_date: string;
  created_by: string | null;
  last_modified_by: string | null;
  size: number;
  file_type: string;
}

export interface DocumentsResponse {
  documents: Document[];
  total_count: number;
  record_id: string;
}

export interface FileUploadRequest {
  fileName: string;
  fileExtension: string;
  contentType: string;
}

export interface MultiFileUploadRequest {
  files: FileUploadRequest[];
  linkedEntityId: string;
  objectName?: string;
}

interface UploadUrl {
  url: string;
  providerType: UploadStorageProviderType;
}

export interface FileUploadResponse {
  success: boolean;
  uploadUrls?: UploadUrl[];
  contentDocumentId?: string;
  contentVersionId?: string;
  error?: string;
}

export interface MultiFileUploadResponse {
  files: FileUploadResponse[];
}

export const storageDocumentsQueryOptions = (
  objectName: string,
  recordId: string,
  limit?: number
) =>
  queryOptions({
    queryKey: ['storage', 'documents', objectName, recordId, limit],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      if (limit) {
        params.append('limit', limit.toString());
      }

      const url = `/api/v1/storage/documents/${objectName}/${recordId}${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await sparkApi.get<DocumentsResponse>(url, {
        signal,
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 1,
    enabled: !!recordId,
  });

export const useUploadFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MultiFileUploadRequest) => {
      const response = await sparkApi.post<MultiFileUploadResponse>('/api/v1/storage/upload', data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate queries for the specific object and record
      if (variables.objectName) {
        queryClient.invalidateQueries({
          queryKey: ['storage', 'documents', variables.objectName, variables.linkedEntityId],
        });
      } else {
        // Fallback to invalidate all storage queries if objectName is not provided
        queryClient.invalidateQueries({
          queryKey: ['storage', 'documents'],
        });
      }
    },
    onError: error => {
      console.error('Upload failed:', error);
    },
  });
};

export const useUploadToS3 = () => {
  return useMutation({
    mutationFn: async ({
      file,
      uploadUrl,
      onProgress,
    }: {
      file: File;
      uploadUrl: string;
      onProgress?: (progress: number) => void;
    }) => {
      const response = await s3Api.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: progressEvent => {
          if (progressEvent.total && onProgress) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        },
      });

      if (response.status !== 200) {
        throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
      }

      return response;
    },
  });
};

export const useUploadToSharepoint = () => {
  return useMutation({
    mutationFn: async ({
      file,
      uploadUrl,
      onProgress,
    }: {
      file: File;
      uploadUrl: string;
      onProgress?: (progress: number) => void;
    }) => {
      try {
        // SharePoint upload sessions require Content-Range header for chunked uploads
        const response = await sharepointApi.put(uploadUrl, file, {
          headers: {
            'Content-Range': `bytes 0-${file.size - 1}/${file.size}`,
          },
          onUploadProgress: progressEvent => {
            if (progressEvent.total && onProgress) {
              const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              onProgress(progress);
            }
          },
        });

        if (response.status !== 200 && response.status !== 201) {
          throw new Error(`SharePoint upload failed: ${response.status} ${response.statusText}`);
        }

        return response;
      } catch (error) {
        console.error('SharePoint upload failed:', error);
        throw error;
      }
    },
  });
};

export enum DownloadDisposition {
  DOWNLOAD = 'download',
  INLINE = 'inline',
}

export const getDownloadUrl = (
  contentDocumentId: string,
  contentVersionId: string,
  fileName: string,
  disposition: DownloadDisposition = DownloadDisposition.DOWNLOAD
): string => {
  const params = new URLSearchParams();
  params.append('fileName', fileName);
  params.append('disposition', disposition);

  return `/api/v1/storage/download/${contentDocumentId}/${contentVersionId}?${params.toString()}`;
};
